/**
 * What kind of thing a message is, for a screen that has to show it at a glance.
 *
 * The log records `purpose` in the system's own vocabulary — nineteen values and growing,
 * because `transcript.ts` deliberately does not close that union and grading files an
 * outcome there. A thread cannot have nineteen looks. This is the collapse from one to the
 * other, and it lives in its own file because it is the sort of mapping that otherwise
 * gets written twice: once for the bubble's colour and once for the word above it, and the
 * two then disagree about what a reminder is.
 *
 * **The emoji fallback is not decoration, it is the older half of the log.** Every row
 * written before F-100 has no `purpose` at all, and a notice written by `notify.ts` files
 * its event name there rather than a message kind. What all of them do have is F-68's
 * marker as their first grapheme (D-26: exactly one, always first), so the marker is a
 * second, older source for the same question and reading it back is what keeps a fortnight
 * of somebody's history from rendering as unlabelled grey boxes.
 *
 * The markers are duplicated here rather than imported for the reason `chat.hint` in
 * `i18n.ts` already gives: `MARKERS` lives in `packages/messaging`, beside the AWS SDK,
 * and importing that package into a browser bundle would ship a DynamoDB client to a phone
 * for the sake of eight characters. Changing one of them means changing this — which is
 * why they are written with their names in a comment, to be grepped for.
 */

/**
 * The looks a bubble can have. Seven, against the log's nineteen purposes.
 *
 * Chosen by what a learner does with the message rather than by which function produced
 * it, which is the same test `MessageKind` in `markers.ts` applies:
 *
 * - `said` is theirs. Nothing else on this list is.
 * - `briefing` is something to read, and it is the one a learner will be *part way
 *   through* when the next message lands — the whole reason the scrolling rule exists.
 * - `question` is something to answer, and `reminder` is the same question asked again.
 *   Separate because a chat that drew them alike would show the same text twice with
 *   nothing saying which one is still open.
 * - `mark` is a verdict on an answer, and is separate from every other reply because it is
 *   the only kind a learner may `DISPUTE` — the composer offers the word against exactly
 *   this.
 * - `reply` is everything else we say back to something they sent.
 * - `summary` ends a day, and `notice` reports on work rather than on learning.
 */
export type BubbleKind =
  | 'said'
  | 'briefing'
  | 'question'
  | 'reminder'
  | 'mark'
  | 'reply'
  | 'summary'
  | 'notice';

/** How an answer was marked. `undefined` for every message that is not a verdict. */
export type Outcome = 'wrong' | 'partial' | 'correct' | 'effortless';

const OUTCOMES: ReadonlySet<string> = new Set<Outcome>([
  'wrong',
  'partial',
  'correct',
  'effortless',
]);

/**
 * `purpose` as the producers write it, collapsed onto the list above.
 *
 * Everything not named here falls through to `reply` when the message is outbound, which
 * is the safe direction: a purpose added next month by somebody adding a reply renders as
 * a reply, rather than as whatever the last entry in a switch happened to be.
 */
const BY_PURPOSE: Readonly<Record<string, BubbleKind>> = {
  briefing: 'briefing',
  question: 'question',
  reminder: 'reminder',
  summary: 'summary',
  /*
   * `notices.ts` and `completed.ts` file their own event name here rather than a message
   * kind, so the four members of `CompletedEvent` are listed by name. They do not have to
   * be: each one carries `✅` or `⚠️` and would reach `notice` through the marker fallback
   * anyway. They are named because the fallback is the *older* path — it exists for rows
   * written before `purpose` did — and a value that can be read directly should not be
   * inferred from the text of the message.
   */
  upload: 'notice',
  research_ready: 'notice',
  research_failed: 'notice',
  curriculum_failed: 'notice',
  source_failed: 'notice',
};

/**
 * The markers from `packages/messaging/src/markers.ts`, by the kind they announce.
 *
 * Written as escapes for the two that carry a variation selector — `⬆️` is U+2B06 U+FE0F
 * and `⚠️` is U+26A0 U+FE0F — because the bare code point renders as a monochrome text
 * glyph on several platforms and, worse here, would fail to match text that carries the
 * selector. Longest first is not needed while no marker is a prefix of another, and the
 * loop below tests whole strings rather than single code points so a surrogate pair is
 * never matched by halves.
 */
const BY_MARKER: readonly (readonly [string, BubbleKind])[] = [
  ['\u{1F4D6}', 'briefing'], // MARKERS.briefing
  ['❓', 'question'], // MARKERS.question
  ['\u{1F4AC}', 'reply'], // MARKERS.reply
  ['⏰', 'reminder'], // MARKERS.reminder
  ['\u{1F319}', 'summary'], // MARKERS.summary
  ['⬆️', 'notice'], // MARKERS.upload
  ['✅', 'notice'], // MARKERS.ready
  ['⚠️', 'notice'], // MARKERS.problem
];

/**
 * Which look this message gets.
 *
 * `direction` decides first and alone. A learner's own message is theirs whatever it says,
 * and the one time that matters is the one time it would go wrong on its own: an answer
 * that quotes the question back — which people do, and which is the reason `mark` in
 * `markers.ts` strips before it prefixes — would otherwise be drawn as a question the
 * system asked.
 */
export function bubbleKind(message: {
  direction: 'in' | 'out';
  purpose?: string | undefined;
  text: string;
}): BubbleKind {
  if (message.direction === 'in') return 'said';
  if (message.purpose !== undefined) {
    const known = BY_PURPOSE[message.purpose];
    if (known) return known;
    if (OUTCOMES.has(message.purpose)) return 'mark';
    return 'reply';
  }
  for (const [marker, kind] of BY_MARKER) {
    if (message.text.startsWith(marker)) return kind;
  }
  return 'reply';
}

/** The verdict on an answer, when this message is one. */
export function outcomeOf(purpose: string | undefined): Outcome | undefined {
  return purpose !== undefined && OUTCOMES.has(purpose) ? (purpose as Outcome) : undefined;
}

/**
 * Whether a bubble is worth offering a subject for.
 *
 * Only the messages whose subject the log actually records. A summary spans every subject
 * of the day and a correction carries none at all (`inbound.ts`'s `send` writes
 * `inReplyTo` and not `nodeId`), so offering the affordance on those would be offering a
 * tap that explains nothing — which teaches a learner that the chips are decorative and
 * costs the feature its whole value on the messages where it works.
 */
export function carriesSubject(kind: BubbleKind): boolean {
  return kind === 'briefing' || kind === 'question' || kind === 'reminder';
}
